import { AppApiError } from '../auth/AppApiClient';
import type { ChatMessage } from '../chat/ChatTypes';
import { RoomClientError } from '../online/RoomClient';
import type { RoomEventParams, RoomSystemAnnouncement } from '../online/roomProtocol';
import { t } from './index';

export function localizedErrorMessage(error: unknown): string {
  if (error instanceof AppApiError) {
    switch (error.code) {
      case 'match-server-unavailable':
        return t('errors:room.unavailable');
      case 'rate-limited':
        return t('errors:room.rateLimited');
      case 'app-server-unavailable':
        return t('errors:network');
      case 'display-name-taken':
        return t('errors:account.displayNameTaken');
      case 'invalid-profile-data':
        return t('errors:account.invalidProfileData');
      case 'onboarding-required':
        return t('errors:account.onboardingRequired');
      case 'profile-not-found':
        return t('errors:account.profileNotFound');
      case 'invalid-token':
      case 'authentication-required':
        return t('errors:account.authenticationRequired');
      case 'forbidden':
        return t('errors:account.forbidden');
      default:
        if (error.status >= 500) return t('errors:network');
        break;
    }
  }
  if (error instanceof RoomClientError) return localizedRoomErrorMessage(error.code);
  console.error('User-facing operation failed.', error);
  return t('errors:unknown');
}

export function localizedRoomErrorMessage(code: string): string {
  switch (code) {
    case 'capacity': return t('errors:room.capacity');
    case 'already-exists': return t('errors:room.alreadyExists');
    case 'unavailable': return t('errors:room.unavailable');
    case 'content-unavailable': return t('errors:room.contentUnavailable');
    case 'invalid-roster': return t('errors:room.invalidRoster');
    case 'room-not-found': return t('errors:room.roomNotFound');
    case 'admission-closed': return t('errors:room.admissionClosed');
    case 'reservation-expired': return t('errors:room.reservationExpired');
    case 'already-joined': return t('errors:room.alreadyJoined');
    case 'room-full': return t('errors:room.roomFull');
    case 'forbidden': return t('errors:room.forbidden');
    case 'empty-roster': return t('errors:room.emptyRoster');
    case 'invalid-match': return t('errors:room.invalidMatch');
    case 'match-not-found': return t('errors:room.matchNotFound');
    case 'lease-mismatch': return t('errors:room.leaseMismatch');
    case 'reservation-unavailable': return t('errors:room.reservationUnavailable');
    case 'reservation-conflict': return t('errors:room.reservationConflict');
    case 'invalid-packet': return t('errors:room.invalidPacket');
    case 'replacement-unavailable': return t('errors:room.replacementUnavailable');
    case 'replacement-ineligible': return t('errors:room.replacementIneligible');
    case 'forfeit-unavailable': return t('errors:room.forfeitUnavailable');
    case 'chat-forbidden': return t('errors:room.chatForbidden');
    case 'resume-expired': return t('errors:room.resumeExpired');
    case 'not-member': return t('errors:room.notMember');
    case 'invalid-config': return t('errors:room.invalidConfig');
    case 'unsupported-bot': return t('errors:room.unsupportedBot');
    case 'invalid-lease': return t('errors:room.invalidLease');
    case 'invalid-reservation': return t('errors:room.invalidReservation');
    case 'auth-pending': return t('errors:room.authPending');
    case 'authentication-required': return t('errors:room.authenticationRequired');
    case 'invalid-operation': return t('errors:room.invalidOperation');
    case 'rate-limited': return t('errors:room.rateLimited');
    case 'request-in-progress': return t('errors:room.requestInProgress');
    case 'invalid-room-response': return t('errors:room.invalidResponse');
    case 'invalid-match-content': return t('errors:room.invalidMatchContent');
    case 'incompatible-room-protocol': return t('errors:room.incompatibleProtocol');
    case 'room-send-failed': return t('errors:room.sendFailed');
    case 'reconnecting': return t('errors:room.reconnecting');
    case 'reconnect-expired': return t('errors:room.reconnectExpired');
    case 'chat-ack-timeout': return t('errors:room.chatAckTimeout');
    case 'chat-send-failed': return t('errors:room.chatSendFailed');
    case 'chat-disconnected': return t('errors:room.chatDisconnected');
    case 'entry-timeout': return t('errors:room.closed.entryTimeout');
    case 'simulation-start-failed': return t('errors:room.closed.simulationStartFailed');
    case 'incomplete-teams': return t('errors:room.closed.incompleteTeams');
    case 'lease-expired': return t('errors:room.closed.leaseExpired');
    case 'lease-unavailable': return t('errors:room.closed.leaseUnavailable');
    case 'match-start-failed': return t('errors:room.closed.matchStartFailed');
    case 'match-cancelled': return t('errors:room.closed.matchCancelled');
    case 'server-shutdown': return t('errors:room.closed.serverShutdown');
    case 'empty-room': return t('errors:room.closed.emptyRoom');
    case 'simulation-ended': return t('errors:room.closed.simulationEnded');
    case 'match-ended': return t('errors:room.closed.matchEnded');
    default: return t('errors:room.requestFailed');
  }
}

export function localizedRoomSystemAnnouncement(event: RoomSystemAnnouncement): ChatMessage {
  const participantName = stringParam(event.params, 'participantName') ?? t('chat:announcements.unknownParticipant');
  let text: string;
  switch (event.code) {
    case 'participant-joined': {
      const role = localizedRole(event.params);
      text = role
        ? t('chat:announcements.participantJoined', { participantName, role })
        : t('chat:announcements.botJoined', { participantName });
      break;
    }
    case 'participant-connection-lost':
      text = t('chat:announcements.participantConnectionLost', { participantName });
      break;
    case 'participant-reconnected':
      text = t('chat:announcements.participantReconnected', { participantName });
      break;
    case 'participant-left':
      text = t('chat:announcements.participantLeft', { participantName });
      break;
    case 'participant-join-failed':
      text = t('chat:announcements.participantJoinFailed', { participantName });
      break;
    case 'bot-joined':
      text = t('chat:announcements.botJoined', { participantName });
      break;
    case 'bot-reconnected':
      text = t('chat:announcements.botReconnected', { participantName });
      break;
    case 'bot-unavailable':
      text = t('chat:announcements.botUnavailable', { participantName });
      break;
  }
  return {
    id: event.id,
    sequence: event.sequence,
    channelId: event.channelId,
    kind: 'system',
    author: null,
    text,
    createdAt: event.createdAt,
  };
}

function localizedRole(params: RoomEventParams | undefined): string | null {
  const role = stringParam(params, 'role');
  if (role === 'player') return t('chat:announcements.roles.player');
  if (role === 'spectator') return t('chat:announcements.roles.spectator');
  return null;
}

function stringParam(params: RoomEventParams | undefined, key: string): string | null {
  const value = params?.[key];
  return typeof value === 'string' && value.trim() ? value : null;
}
